// The Swish pay-now link is the key to a customer's unpaid insurance invoices. Links to the pay-now page carry it in
// the URL fragment, which no server sees. src/index.tsx imports this module first, so the fragment leaves the URL before
// Sentry, Hotjar or any page code can read it; the page reads the link back with readPayNowLink().
export const PAY_NOW_PATH = '/outstanding-payments/pay';

const STORAGE_KEY = 'pay-now-link';

// The tab's sessionStorage keeps the link across reloads; memory covers a browser that refuses storage.
let linkInMemory: string | null = null;

export const readPayNowLink = (): string | null => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) ?? linkInMemory;
  } catch {
    return linkInMemory;
  }
};

export const forgetPayNowLink = (): void => {
  linkInMemory = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing stored
  }
};

// The router also matches the path with a trailing slash.
const isPayNowPath = (pathname: string): boolean => pathname.replace(/\/+$/, '') === PAY_NOW_PATH;

// True when it took a link from the URL.
export const takePayNowFragment = (location: Location = window.location, history: History = window.history): boolean => {
  if (!isPayNowPath(location.pathname) || location.hash.length < 2) return false;

  const link = location.hash.slice(1);
  // The Swish app comes back with the page's 1-hour link; it must not replace the SMS link the tab already has.
  const isSwishReturn = new URLSearchParams(location.search).has('payment_id');

  if (!isSwishReturn || !readPayNowLink()) {
    linkInMemory = link;
    try {
      sessionStorage.setItem(STORAGE_KEY, link);
    } catch {
      // kept in memory only
    }
  }

  history.replaceState(history.state, '', `${location.pathname}${location.search}`);

  return true;
};

takePayNowFragment();

// A link opened in a tab that already shows the page changes only the fragment: take it, then load the page again.
window.addEventListener('hashchange', () => {
  if (takePayNowFragment()) window.location.reload();
});
